import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Thermometer, Droplets, UtensilsCrossed, Gauge, Dna, CalendarDays, Bell, FileDown } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { useCart, COMING_SOON } from "@/context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Spec = ({ icon: Icon, label, value }) =>
    value ? (
        <div className="border border-red-950/40 bg-black/40 p-3">
            <p className="flex items-center gap-2 font-mono2 text-[9px] uppercase tracking-[0.2em] text-red-500">
                <Icon className="h-3 w-3" /> {label}
            </p>
            <p className="mt-1.5 text-sm text-bone">{value}</p>
        </div>
    ) : null;

export const AnimalDetailModal = ({ product, onClose }) => {
    const { add } = useCart();
    const [waitEmail, setWaitEmail] = useState("");
    const [waitDone, setWaitDone] = useState(false);
    const [waitBusy, setWaitBusy] = useState(false);

    const joinWaitlist = async () => {
        if (!waitEmail) return;
        setWaitBusy(true);
        try {
            await axios.post(`${API}/waitlist`, { product_id: product.id, email: waitEmail });
            setWaitDone(true);
            toast.success("You're on the waitlist — we'll email you first.");
        } catch {
            toast.error("Could not join the waitlist — try again.");
        } finally {
            setWaitBusy(false);
        }
    };

    const buyCareSheet = () => {
        add({
            id: `caresheet:${product.id}`,
            name: `Care Sheet — ${product.name}`,
            price: 10,
            image_url: product.image_url,
            category: "caresheet",
            stock: 99,
        });
        toast.success("Care sheet added — $10");
        onClose();
    };

    return (
        <AnimatePresence>
            {product && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80] flex items-end justify-center bg-black/80 backdrop-blur-sm sm:items-center sm:p-6"
                    onClick={onClose}
                    data-testid="animal-detail-modal"
                >
                    <motion.div
                        initial={{ y: 60, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 60, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 220, damping: 26 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto border border-red-900/50 bg-coal"
                    >
                        <button
                            data-testid="animal-detail-close"
                            onClick={onClose}
                            className="absolute right-4 top-4 z-10 border border-red-900/60 bg-black/70 p-2 text-ash transition-colors duration-300 hover:text-bone"
                        >
                            <X className="h-4 w-4" />
                        </button>

                        <div className="grid grid-cols-1 md:grid-cols-2">
                            <div className="relative">
                                <img
                                    src={product.image_url}
                                    alt={product.name}
                                    className="h-64 w-full object-cover md:h-full md:min-h-[480px]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-coal/90 via-transparent to-transparent md:bg-gradient-to-r" />
                            </div>
                            <div className="p-7">
                                <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-red-500">
                                    {product.category} · {product.rarity}
                                </p>
                                <h3 className="mt-2 font-display text-2xl font-bold text-bone">
                                    {product.name}
                                </h3>
                                <p className="font-editorial text-lg italic text-ash">
                                    {product.species || product.morph}
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-ash">
                                    {product.description}
                                </p>

                                <div className="mt-6 grid grid-cols-2 gap-2">
                                    <Spec icon={Gauge} label="Care Level" value={product.care_level} />
                                    <Spec icon={Thermometer} label="Temperature" value={product.temperature} />
                                    <Spec icon={Droplets} label="Humidity" value={product.humidity} />
                                    <Spec icon={UtensilsCrossed} label="Diet" value={product.diet} />
                                    <Spec icon={CalendarDays} label="Birth Year" value={product.birth_year} />
                                    <Spec icon={Dna} label="Lineage" value={product.lineage} />
                                </div>

                                {product.temperament && (
                                    <p className="mt-4 border-l-2 border-red-800 pl-3 font-editorial text-sm italic text-ash">
                                        Temperament — {product.temperament}
                                    </p>
                                )}

                                <div className="mt-7 border-t border-red-950/50 pt-5">
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono2 text-xl text-bone">
                                            ${product.price.toLocaleString()}
                                        </span>
                                        {!COMING_SOON && product.stock > 0 ? (
                                            <button
                                                data-testid="animal-detail-add-to-cart"
                                                onClick={() => {
                                                    add(product);
                                                    toast.success(`${product.name} added to your enclosure`);
                                                    onClose();
                                                }}
                                                className="flex items-center gap-2 bg-crimson px-6 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-red-700"
                                            >
                                                <Plus className="h-3.5 w-3.5" /> Add to Enclosure
                                            </button>
                                        ) : waitDone ? (
                                            <span className="border border-emerald-800/60 bg-emerald-950/40 px-4 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em] text-emerald-400" data-testid="waitlist-joined">
                                                On the waitlist
                                            </span>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    data-testid="waitlist-email-input"
                                                    type="email"
                                                    placeholder="your@email.com"
                                                    value={waitEmail}
                                                    onChange={(e) => setWaitEmail(e.target.value)}
                                                    className="w-44 border border-amber-800/60 bg-black/50 px-3 py-2.5 text-xs text-bone placeholder:text-ash/40 outline-none focus:border-amber-600"
                                                />
                                                <button
                                                    data-testid="waitlist-join-button"
                                                    onClick={joinWaitlist}
                                                    disabled={waitBusy}
                                                    className="flex items-center gap-2 border border-amber-700/70 bg-amber-900/30 px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-400 transition-colors duration-300 hover:bg-amber-800/40 disabled:opacity-50"
                                                >
                                                    <Bell className="h-3.5 w-3.5" /> {waitBusy ? "Joining…" : "Join Waitlist"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {product.category !== "terrariums" && (
                                        <a
                                            data-testid="care-sheet-preview-link"
                                            href={`${API}/care-sheets/${product.category === "caresheets" ? product.linked_product || product.id : product.id}/preview.png`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-4 flex items-center gap-3 border border-red-950/60 p-2 transition-colors duration-300 hover:border-red-700"
                                        >
                                            <img
                                                src={`${API}/care-sheets/${product.category === "caresheets" ? product.linked_product || product.id : product.id}/preview.png`}
                                                alt={`Watermarked first-page preview of the ${product.name} care sheet`}
                                                className="h-20 w-14 border border-red-900/40 object-cover object-top"
                                                loading="lazy"
                                            />
                                            <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-ash">
                                                Preview first page
                                                <span className="mt-1 block text-red-500">Watermarked · full PDF after purchase</span>
                                            </span>
                                        </a>
                                    )}
                                    {!COMING_SOON && product.category !== "caresheets" && (
                                        <button
                                            data-testid="care-sheet-buy-button"
                                            onClick={buyCareSheet}
                                            className="mt-4 flex w-full items-center justify-center gap-2 border border-red-950/60 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:border-red-700 hover:text-bone"
                                        >
                                            <FileDown className="h-3.5 w-3.5" /> Species Care Sheet — $10 PDF download
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
